import React from 'react';
import { ApIcon, colors } from '@alixpartners/ui-components';
import moment from 'moment';
import {
  IAutoClassificationFileStatus,
  IFileData,
  IFileDataType,
  IFileStatus,
} from 'utils/types';
import {
  ISettingItem,
  ISettingsResponse,
  ISettings,
  IPeriod,
} from '../../utils/types';
import styled from 'styled-components';

const MONTHS_SHORT = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

export const MONTHS_INDEXES = [
  {
    value: '7',
    label: 'July',
  },
  {
    value: '8',
    label: 'August',
  },
  {
    value: '9',
    label: 'September',
  },
  {
    value: '10',
    label: 'October',
  },
  {
    value: '11',
    label: 'November',
  },
  {
    value: '12',
    label: 'December',
  },
  {
    value: '1',
    label: 'January',
  },
  {
    value: '2',
    label: 'February',
  },
  {
    value: '3',
    label: 'March',
  },
  {
    value: '4',
    label: 'April',
  },
  {
    value: '5',
    label: 'May',
  },
  {
    value: '6',
    label: 'June',
  },
];

export const createPeriodsFromRange = (
  availableRange: IPeriod,
  startPeriod: number
) => {
  if (!availableRange || !startPeriod) {
    return [];
  }
  let currentStartPeriod = startPeriod;
  let currentStartYear = availableRange.startYear;
  let currentEndYear = 0;
  let currentEndPeriod = startPeriod === 1 ? 12 : startPeriod - 1;
  if (availableRange.startPeriod < startPeriod) {
    currentStartYear = availableRange.startYear - 1;
  }
  currentEndYear = startPeriod === 1 ? currentStartYear : currentStartYear + 1;

  const ranges = [
    {
      startPeriod: currentStartPeriod,
      endPeriod: currentEndPeriod,
      startYear: currentStartYear,
      endYear: currentEndYear,
    },
  ];
  while (currentEndYear < availableRange.endYear) {
    currentStartYear =
      currentEndPeriod === 12 ? currentStartYear + 1 : currentEndYear;
    currentStartPeriod = currentEndPeriod + 1 <= 12 ? currentEndPeriod + 1 : 1;
    currentEndPeriod =
      currentStartPeriod - 1 >= 1 ? currentStartPeriod - 1 : 12;
    currentEndYear =
      currentEndPeriod === 12
        ? currentStartYear
        : Math.min(currentEndYear + 1, availableRange.endYear);
    ranges.push({
      startPeriod: currentStartPeriod,
      endPeriod: currentEndPeriod,
      startYear: currentStartYear,
      endYear: currentEndYear,
    });
  }
  const lastRange = ranges[ranges.length - 1];
  if (lastRange.endPeriod < availableRange.endPeriod) {
    ranges.push({
      startPeriod: lastRange.endPeriod + 1,
      endPeriod: lastRange.endPeriod,
      startYear: lastRange.endYear,
      endYear: lastRange.endYear + 1,
    });
  }
  return ranges;
};

export const getSelectedIds = (data: ISettingsResponse): ISettings => {
  return {
    currencyId: (data.currencies || []).find(
      (c: ISettingItem) => c.default === true
    )?.id,
    unitId: (data.units || []).find((c: ISettingItem) => c.default === true)
      ?.id,
    decimalId: (data.decimals || []).find(
      (c: ISettingItem) => c.default === true
    )?.id,
    calendarYear: data.selectedYear,
    clientName: data.clientName || 'Client Name',
  };
};

export const getPeriodStr = (period?: IPeriod): string => {
  if (!period) {
    return '';
  }
  const isCalendarYear = period.startPeriod === 1;
  if (isCalendarYear) {
    return `${period.startYear}`;
  }
  const startPeriodStr = `${MONTHS_SHORT[period.startPeriod]} ${
    period.startYear
  }`;
  const endPeriodStr = `${MONTHS_SHORT[period.endPeriod]} ${period.endYear}`;
  return `${startPeriodStr} - ${endPeriodStr}`;
};

export const ErrorIcon = styled(ApIcon)`
  color: ${colors.red2};
`;

export const getFileStateText = ({
  status,
  autoClassificationStatus,
}: {
  status: IFileStatus;
  autoClassificationStatus?: IAutoClassificationFileStatus;
}) => {
  if (autoClassificationStatus === IAutoClassificationFileStatus.IN_PROGRESS) {
    return 'Auto classification is in progress...';
  }
  if (autoClassificationStatus === IAutoClassificationFileStatus.CLASSIFIED) {
    return 'Unpublished, auto classified';
  }
  if (autoClassificationStatus === IAutoClassificationFileStatus.FAILED) {
    return 'Unpublished, auto classification failed';
  }
  return status;
};

export const getStatusIcon = ({
  status,
  autoClassificationStatus,
  isApplication,
  isActive,
}: {
  status: IFileStatus;
  autoClassificationStatus?: IAutoClassificationFileStatus;
  isApplication?: boolean;
  isActive?: boolean;
}) => {
  const iconColor = isActive ? colors.white : colors.grey8;

  if (isApplication) {
    return status === IFileStatus.PUBLISHED ? (
      <ApIcon iconName="check" iconColor={iconColor} />
    ) : (
      <ApIcon iconName="pie_chart_outline" iconColor={iconColor} />
    );
  }
  if (autoClassificationStatus === IAutoClassificationFileStatus.IN_PROGRESS) {
    return (
      <ApIcon iconName="lock_outlined" iconColor={colors.grey8} iconSize={24} />
    );
  }
  if (
    status === IFileStatus.ERROR ||
    status === IFileStatus.INVALID ||
    status === IFileStatus.UPLOAD_FAILED
  ) {
    return <ErrorIcon iconName="error_outline" iconColor={iconColor} />;
  }
  if (status === IFileStatus.UPLOADED) {
    return <ApIcon iconName="dynamic_feed" iconColor={iconColor} />;
  }
  if (status === IFileStatus.PUBLISHED) {
    return <ApIcon iconName="check" iconColor={iconColor} />;
  }
  return <ApIcon iconName="cloud_upload" iconColor={iconColor} />;
};

export const getUploadedText = (status: IFileStatus) =>
  status === IFileStatus.PUBLISHED ? 'Published by' : 'Uploaded by';

export const getPublishedTime = (dateString: string) => {
  const today = moment(); // Local time
  const momentDate = moment.utc(dateString).local(); // Convert UTC to local time

  return momentDate.isSame(today, 'day')
    ? `at ${momentDate.format('H:mma')} today`
    : `at ${momentDate.format('H:mma')} on ${momentDate.format('MMM D YYYY')}`;
};

export const getFileDataTypeFromLocation = (location: any) => {
  if (location.pathname.includes(getSettingsDataLink('revenue'))) {
    return IFileDataType.REVENUE;
  }
  if (location.pathname.includes(getSettingsDataLink('third-party-spend'))) {
    return IFileDataType.THIRD_PARTY_SPEND;
  }
  if (location.pathname.includes(getSettingsDataLink('personnel-spend'))) {
    return IFileDataType.PERSONNEL_SPEND;
  }
  if (location.pathname.includes(getSettingsDataLink('applications'))) {
    return IFileDataType.APPLICATIONS;
  }
  if (location.pathname.includes(getSettingsDataLink('products'))) {
    return IFileDataType.PRODUCTS;
  }
  return IFileDataType.REVENUE;
};

export const getFileTypeStatus = (files: IFileData[]): IFileStatus => {
  if (files.length === 0) {
    return IFileStatus.NOT_UPLOADED;
  }
  if (
    files.find(
      (f) =>
        f.fileState === IFileStatus.ERROR || f.fileState === IFileStatus.INVALID
    )
  ) {
    return IFileStatus.ERROR;
  }
  if (files.every((f) => f.fileState === IFileStatus.PUBLISHED)) {
    return IFileStatus.PUBLISHED;
  }
  return IFileStatus.UPLOADED;
};

export const getSelectedFiles = (
  files: IFileData[],
  fileDataType: IFileDataType
) => files.filter((f: IFileData) => f.fileDataType === fileDataType);

export const getSettingsDataLink = (url: string) => `/settings/data/${url}`;
